import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import api from '../services/api';

const PermissionMatrix = ({ permissions, onChange, readOnly = false }) => {
  const [allPermissions, setAllPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllPermissions();
  }, []);

  const fetchAllPermissions = async () => {
    try {
      const response = await api.get('/api/permissions/all');
      setAllPermissions(response.data.grouped || {});
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permission) => {
    if (readOnly) return;

    const newPermissions = permissions.includes(permission)
      ? permissions.filter(p => p !== permission)
      : [...permissions, permission];

    onChange(newPermissions);
  };

  const handleModuleToggle = (module, modulePerms) => {
    if (readOnly) return;

    const allSelected = modulePerms.every(p => permissions.includes(p));
    let newPermissions;

    if (allSelected) {
      // Deselect all in module
      newPermissions = permissions.filter(p => !modulePerms.includes(p));
    } else {
      // Select all in module
      const toAdd = modulePerms.filter(p => !permissions.includes(p));
      newPermissions = [...permissions, ...toAdd];
    }

    onChange(newPermissions);
  };

  const getModuleStats = (modulePerms) => {
    const selected = modulePerms.filter(p => permissions.includes(p)).length;
    const total = modulePerms.length;
    return { selected, total, allSelected: selected === total };
  };

  const formatPermissionName = (perm) => {
    const parts = perm.split('.');
    return parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
  };

  const getPermissionColor = (perm) => {
    if (perm.includes('delete')) return 'error';
    if (perm.includes('edit') || perm.includes('create') || perm.includes('manage')) return 'warning';
    return 'info';
  };

  if (loading) {
    return <Typography>Loading permissions...</Typography>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Select permissions for this role
      </Typography>

      {Object.keys(allPermissions).length === 0 && (
        <Alert severity="info">No permissions available</Alert>
      )}

      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Object.entries(allPermissions).map(([module, modulePerms]) => {
          const stats = getModuleStats(modulePerms);

          return (
            <Grid item xs={12} sm={6} md={4} key={module}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  height: '100%',
                  border: stats.allSelected ? 2 : 0,
                  borderColor: 'primary.main'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                    {module}
                  </Typography>
                  <Chip
                    label={`${stats.selected}/${stats.total}`}
                    size="small"
                    color={stats.allSelected ? 'primary' : 'default'}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={stats.allSelected}
                      indeterminate={stats.selected > 0 && !stats.allSelected}
                      onChange={() => handleModuleToggle(module, modulePerms)}
                      disabled={readOnly}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight="bold">
                      All {module}
                    </Typography>
                  }
                />

                <Divider sx={{ my: 1 }} />

                <FormGroup>
                  {modulePerms.map((perm) => (
                    <FormControlLabel
                      key={perm}
                      control={
                        <Checkbox
                          checked={permissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                          disabled={readOnly}
                          size="small"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">
                            {formatPermissionName(perm)}
                          </Typography>
                          <Chip
                            label={perm.split('.')[1]}
                            size="small"
                            color={getPermissionColor(perm)}
                            sx={{ height: 16, fontSize: '0.65rem' }}
                          />
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Selected:</strong> {permissions.length} permissions
        </Typography>
      </Box>
    </Box>
  );
};

export default PermissionMatrix;
